import { Grid } from "@material-ui/core";
import AppHeader from "@saleor/components/AppHeader";
import Container from "@saleor/components/Container";
import MyTypography from "@saleor/components/MyTypography";
import PageHeader from "@saleor/components/PageHeader";
import { defaultListSettings } from "@saleor/config";
import { createOrderUrl } from "@saleor/createOrder/urls";
import CartSearchBar from "@saleor/createOrderExport/components/CartSearchBar";
import EmptyCartTable from "@saleor/createOrderExport/components/EmptyCartTable";
import {
  shoppingCartListUrl,
  ShoppingCartQueryParams
} from "@saleor/createOrderExport/urls";
import { useExportCartsSearchQuery } from "@saleor/graphql";
import useListSettings from "@saleor/hooks/useListSettings";
import useLocalPaginator, {
  useLocalPaginationState
} from "@saleor/hooks/useLocalPaginator";
import useNavigator from "@saleor/hooks/useNavigator";
import { makeStyles } from "@saleor/macaw-ui";
import { ListViews } from "@saleor/types";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { getSortParams, getSortUrlVariables } from "@saleor/utils/sort";
import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import useRouter from "use-react-router";

import CartSummary from "../../components/CartSummary";
import CartTable from "../../components/CartTable";
import { getSortQueryShoppingCartVariables } from "./sort";

const useStyle = makeStyles(
  () => ({
    header: {
      marginBottom: 0
    },
    headerWrapper: {
      alignItems: "center"
    },
    inputWrapper: {
      alignItems: "center",
      marginBottom: 24,
      "& .MuiGrid-item": {
        padding: "0 1.6rem"
      }
    }
  }),
  { name: "ShoppingCartExport" }
);

interface ShoppingCartExportProps {
  params: ShoppingCartQueryParams;
}

const ShoppingCartExport: React.FC<ShoppingCartExportProps> = ({ params }) => {
  const intl = useIntl();
  const { history } = useRouter();
  const [searchId, setSearchId] = useState("");
  const navigate = useNavigator();
  const classes = useStyle();
  const { updateListSettings, settings } = useListSettings(
    ListViews.ORDER_EXPORT_SOLD_TO_LIST
  );

  useEffect(() => {
    updateListSettings(
      "rowNumber",
      defaultListSettings.ORDER_EXPORT_SOLD_TO_LIST.rowNumber
    );
  }, []);

  const [
    valuesPaginationState,
    setValuesPaginationState
  ] = useLocalPaginationState(settings?.rowNumber);

  const resetPaginationState = () => {
    setValuesPaginationState({
      first: settings?.rowNumber,
      after: undefined,
      before: undefined
    });
  };
  const paginateValues = useLocalPaginator(setValuesPaginationState);
  const handleBack = () => {
    history.goBack();
  };

  const queryVariables = React.useMemo(
    () => ({
      ...valuesPaginationState,
      filter: searchId
        ? {
            inputSearch: searchId
          }
        : undefined,
      sortBy: getSortQueryShoppingCartVariables(params)
    }),
    [searchId, settings.rowNumber, valuesPaginationState, params]
  );

  const { data: exportCartsData, loading } = useExportCartsSearchQuery({
    variables: queryVariables
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginateValues(
    exportCartsData?.exportCartsSearch?.exportCarts?.pageInfo,
    valuesPaginationState
  );
  const lastPaginationState = () => {
    setValuesPaginationState({
      last:
        exportCartsData?.exportCartsSearch?.exportCarts?.latestPageItemNumber
    });
  };
  const handlePreviousFirstPage = () => {
    resetPaginationState();
  };
  const handleNextLastPage = () => {
    lastPaginationState();
  };
  const handleSort = field => {
    resetPaginationState();
    navigate(
      shoppingCartListUrl({
        ...params,
        ...getSortUrlVariables(field, params)
      }),
      { replace: true }
    );
  };
  useEffect(() => {
    resetPaginationState();
  }, [settings?.rowNumber, searchId]);

  const carts = useMemo(
    () => mapEdgesToItems(exportCartsData?.exportCartsSearch?.exportCarts),
    [exportCartsData]
  );
  const handleSeach = (id: string) => {
    setSearchId(id);
  };
  const handleReset = () => {
    setSearchId("");
  };

  return (
    <>
      <Container>
        <AppHeader onBack={handleBack}>
          <MyTypography variant="body3">
            {intl.formatMessage({ defaultMessage: "กลับไป" })}
          </MyTypography>
        </AppHeader>
        <Grid container className={classes.headerWrapper} spacing={4}>
          <Grid item xs={3}>
            <PageHeader
              title={intl.formatMessage({ defaultMessage: "Shopping Cart" })}
              className={classes.header}
            />
          </Grid>
          <Grid item xs={9}>
            {carts?.length ? (
              <MyTypography variant="body3">
                <FormattedMessage defaultMessage="ค้นหาเลขที่ PI หรือ Sold to" />
              </MyTypography>
            ) : null}
          </Grid>
        </Grid>
        <Grid container className={classes.inputWrapper} spacing={4}>
          <Grid item xs={3}>
            <MyTypography variant="h7">
              {intl.formatMessage({ defaultMessage: "Summary" })}
            </MyTypography>
          </Grid>
          <Grid item xs={9}>
            {carts?.length ? (
              <CartSearchBar
                onChange={handleSeach}
                onReset={handleReset}
                searchId={searchId}
              />
            ) : null}
          </Grid>
        </Grid>
        <Grid container spacing={4}>
          <Grid item xs={3}>
            <CartSummary exportCartsData={exportCartsData} />
          </Grid>
          <Grid item xs={9}>
            {carts?.length || loading ? (
              <CartTable
                disabled={loading}
                data={carts}
                loading={loading}
                settings={settings}
                onUpdateListSettings={updateListSettings}
                onNextPage={loadNextPage}
                onPreviousPage={loadPreviousPage}
                onNextLastPage={handleNextLastPage}
                onPreviousFirstPage={handlePreviousFirstPage}
                totalCount={
                  exportCartsData?.exportCartsSearch?.exportCarts?.totalCount
                }
                latestPageItemNumber={
                  exportCartsData?.exportCartsSearch?.exportCarts
                    ?.latestPageItemNumber
                }
                pageInfo={pageInfo}
                onRowClick={() => null}
                onSort={handleSort}
                sort={getSortParams(params)}
              />
            ) : (
              <EmptyCartTable onBack={() => navigate(createOrderUrl)} />
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default ShoppingCartExport;
